import React from 'react';

type AlertVariant = 'error' | 'warning' | 'success' | 'info';

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children?: React.ReactNode;
  className?: string;
}

const boxClasses: Record<AlertVariant, string> = {
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  success: 'bg-green-50 border-green-200 text-green-800',
  info: 'bg-brand-50 border-brand-200 text-brand-ink-deep',
};

const iconColor: Record<AlertVariant, string> = {
  error: 'text-red-500',
  warning: 'text-amber-500',
  success: 'text-green-600',
  info: 'text-brand-ink',
};

function AlertIcon({ variant, className }: { variant: AlertVariant; className: string }) {
  const common = {
    className,
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (variant === 'success') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }
  if (variant === 'info') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

export const Alert: React.FC<AlertProps> = ({ variant = 'error', title, children, className = '' }) => (
  <div
    role="alert"
    className={['flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm', boxClasses[variant], className].join(' ')}
  >
    <AlertIcon variant={variant} className={['mt-0.5 shrink-0', iconColor[variant]].join(' ')} />
    <div className="min-w-0">
      {title && <p className="font-medium">{title}</p>}
      {children && <div className={title ? 'mt-0.5 text-xs opacity-90' : ''}>{children}</div>}
    </div>
  </div>
);
